/**
 * fetch 封装：同源、带 cookie、自动附 CSRF 头、401 自动刷新并重放。
 * 见 design/04-frontend.md「数据层」。
 */

import type { ApiErrorBody } from './types'

const BASE = import.meta.env.VITE_API_BASE ?? ''
const API = `${BASE}/api/v1`

/** 给 <a href> 用的完整地址（比如下载），不走 fetch 的时候 */
export const apiUrl = (path: string) => `${API}${path}`
const REFRESH_PATH = '/auth/refresh'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, string[]>

  constructor(status: number, code: string, message: string, details?: Record<string, string[]>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  /**
   * 取某个字段的第一条错误，用于表单内联提示。
   *
   * 后端的 details 有两种：schema 校验给的是中文句子（「用户名只能包含…」），
   * service 层给的是机器码（`taken`、`same`）。机器码不该原样显示给人看，
   * 这时换成顶层的 message（「用户名已被占用」）。
   */
  fieldError(field: string): string | undefined {
    const detail = this.details?.[field]?.[0]
    if (detail === undefined) return undefined
    return /^[a-z_ |]+$/.test(detail) ? this.message : detail
  }

  get isAuthError(): boolean {
    return this.status === 401
  }
}

export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * refresh 端点校验的是 refresh token 的 CSRF，其余写请求用 access 的。
 * 两个 CSRF cookie 都是可读的（path=/）。
 */
function csrfTokenFor(path: string): string | undefined {
  return readCookie(path.startsWith(REFRESH_PATH) ? 'csrf_refresh_token' : 'csrf_access_token')
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** 内部使用：标记这是刷新后的重放，避免无限递归 */
  _retried?: boolean
}

/** 并发请求共享同一个 refresh promise，避免重复刷新 */
let refreshPromise: Promise<boolean> | null = null

async function runRefresh(): Promise<boolean> {
  try {
    const response = await fetch(`${API}${REFRESH_PATH}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-TOKEN': csrfTokenFor(REFRESH_PATH) ?? '' },
    })
    return response.ok
  } catch {
    return false
  }
}

function refreshOnce(): Promise<boolean> {
  refreshPromise ??= runRefresh().finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

type UnauthorizedHandler = () => void
let onUnauthorized: UnauthorizedHandler = () => {}

/** 由 providers 注册：刷新失败时清空用户缓存并跳登录 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler) {
  onUnauthorized = handler
}

/**
 * 响应体里没有 message 时的兜底文案。
 *
 * 这个模块不在 React 树里，拿不到 useT()，所以和 unauthorized 处理一样
 * 由 providers 注册进来；没注册时退回英文，至少不是一句半截的中文。
 */
let fallbackMessage = 'Request failed'

export function setFallbackMessage(text: string) {
  fallbackMessage = text
}

async function parseError(response: Response): Promise<ApiError> {
  let code = 'internal_error'
  let message = response.statusText || fallbackMessage
  let details: Record<string, string[]> | undefined

  try {
    const body = (await response.json()) as Partial<ApiErrorBody>
    if (body?.error) {
      code = body.error.code ?? code
      message = body.error.message ?? message
      details = body.error.details
    }
  } catch {
    /* 非 JSON 响应，保留默认文案 */
  }
  return new ApiError(response.status, code, message, details)
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, _retried, ...rest } = options
  const method = (rest.method ?? 'GET').toUpperCase()

  const finalHeaders = new Headers(headers)
  let payload: BodyInit | undefined

  if (body instanceof FormData) {
    payload = body // 让浏览器自己带 multipart boundary
  } else if (body !== undefined) {
    finalHeaders.set('Content-Type', 'application/json')
    payload = JSON.stringify(body)
  }

  if (method !== 'GET' && method !== 'HEAD') {
    const token = csrfTokenFor(path)
    if (token) finalHeaders.set('X-CSRF-TOKEN', token)
  }

  const response = await fetch(`${API}${path}`, {
    ...rest,
    method,
    credentials: 'include',
    headers: finalHeaders,
    body: payload,
  })

  if (response.ok) {
    if (response.status === 204) return undefined as T
    return (await response.json()) as T
  }

  const error = await parseError(response)

  // access token 过期：静默刷新一次后重放原请求
  if (error.code === 'token_expired' && !_retried && !path.startsWith(REFRESH_PATH)) {
    const refreshed = await refreshOnce()
    if (refreshed) {
      return request<T>(path, { ...options, _retried: true })
    }
    onUnauthorized()
  }

  if (error.status === 401 && path.startsWith(REFRESH_PATH)) {
    onUnauthorized()
  }

  throw error
}

export const api = {
  get: <T>(path: string, init?: RequestOptions) => request<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: 'PATCH', body }),
  delete: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: 'DELETE', body }),
}

/** 拼查询串，跳过 undefined / null / 空串 */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value))
    }
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

/**
 * 带上传进度的 multipart POST（视频可能有 100 MB，fetch 拿不到上传进度）。
 *
 * 行为和 request() 对齐：带 Cookie 与 CSRF 头；access 过期时静默刷新一次再重放；错误同样是 ApiError。
 */
export function uploadWithProgress<T>(
  path: string,
  form: FormData,
  onProgress: (fraction: number) => void,
  retried = false,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API}${path}`)
    xhr.withCredentials = true
    const token = csrfTokenFor(path)
    if (token) xhr.setRequestHeader('X-CSRF-TOKEN', token)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total)
    }
    xhr.onerror = () => reject(new ApiError(0, 'network_error', fallbackMessage))
    xhr.onload = () => {
      let body: unknown = null
      try {
        body = JSON.parse(xhr.responseText)
      } catch {
        /* 非 JSON 响应 */
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as T)
        return
      }
      const error = (body as Partial<ApiErrorBody> | null)?.error
      const failure = new ApiError(
        xhr.status,
        error?.code ?? 'internal_error',
        error?.message ?? (xhr.statusText || fallbackMessage),
        error?.details,
      )
      if (failure.code === 'token_expired' && !retried) {
        refreshOnce().then((refreshed) => {
          if (!refreshed) {
            onUnauthorized()
            reject(failure)
            return
          }
          uploadWithProgress<T>(path, form, onProgress, true).then(resolve, reject)
        }, reject)
        return
      }
      reject(failure)
    }
    xhr.send(form)
  })
}
